import React from 'react'
import styled from 'styled-components'
import {Button} from '../../components/library/buttons'
import {AF_CSV_MAX_UPLOAD_FILE_SIZE_BYTES} from '../../common/types/constants'
import {
  MAX_USERS_PER_MESSAGE,
  ARRAY_SEPARATOR
} from '../../common/csv-import/af-bulk-send'
import {addDays} from 'date-fns'
import {useContainers} from '../../hooks/use-containers'
import {useAuth} from '../../lib/auth'
import {v4} from 'uuid'

const HelpDiv = styled.div`
  line-height: 1.8;
  background-color: ${props => props.theme.grey05};
  padding: 20px;
  margin-bottom: 20px;
  border-radius: ${props => props.theme.borderRadius};
  position: relative;

  > .close-wrap {
    position: absolute;
    top: 0;
    padding: 10px;
    right: 0;
  }
`

export const Help = ({
  environment,
  showHelp
}: {
  environment: {id: string; organisationId: string}
  showHelp: boolean
}) => {
  const {authState} = useAuth()
  const email = authState.authenticated && authState.email
  const [streamContainers] = useContainers(
    environment.organisationId,
    environment.id
  )
  const streamIds = [...new Set(streamContainers?.map(c => c.streamId))]
  return (
    <>
      {showHelp && (
        <HelpDiv>
          <h3>Formatting your CSV</h3>

          <p>CSV rows are keyed by the following prefixes</p>
          <ul>
            <li>
              <p style={{margin: '0'}}>
                <b>target.attributes:</b> The profile fields to match for the
                row, more than one can be specified in a row. When more than one
                is specified they are matched with an "and" operator. For
                example, if targeting users by Timezone the header must be{' '}
                <code>target.attributes.timezone</code> and the corresponding
                value in each row should be the value to match i.e.{' '}
                <code>Pacific/Auckland</code>. You can target users by any
                profile fields including{' '}
                <a
                  href={`/${environment.organisationId}/${environment.id}/configuration/custom-fields`}
                >
                  custom fields.
                </a>{' '}
                Each CSV row can only match up to {MAX_USERS_PER_MESSAGE} users,
                if a row matches more it will be skipped.
              </p>
            </li>
            <li>
              <b>payload.cardOptions:</b> The card options to use for the row.
              Must be one of the following:{' '}
              <ul>
                <li>payload.cardOptions.priority</li>
                <li>payload.cardOptions.expires</li>
                <li>payload.cardOptions.expiresInterval</li>
                <li>payload.cardOptions.embargo</li>
                <li>payload.cardOptions.targetStreamIds</li>
                <li>payload.cardOptions.targetPlatforms</li>
              </ul>
            </li>
            <li>
              <b>payload.variables:</b> The variables to use for sending the
              Action Flow in the format{' '}
              <code>payload.variables.myVariableName</code>
            </li>
            <li>
              <b>payload.metadata:</b> Arbitrary metadata to send with the
              Action Flow in the format{' '}
              <code>payload.metadata.myMetadataName</code>
            </li>
            <li>
              <b>payload.notificationDetail:</b> Arbitrary metadata to send with
              the any triggered notifications in the format{' '}
              <code>payload.notificationDetail.myNotificationDetailName</code>
            </li>
            <li>
              <p style={{margin: '0'}}>
                <b>flowInvocationId:</b> A unique identifier for the{' '}
                <a href="https://documentation.atomic.io/api/action-flow-api#flows">
                  Action Flow
                </a>{' '}
                triggered by this row.
              </p>
            </li>
            <li>
              <p style={{margin: '0'}}>
                <b>linked:</b> Whether the cards created should be{' '}
                <a href="https://documentation.atomic.io/api/action-flow-api#linked-cards-option-when-targeting-specific-users">
                  linked
                </a>
                .
              </p>
            </li>
          </ul>

          <p>
            When using array values for example,{' '}
            <code>payload.cardOptions.targetStreamIds</code>, the value should
            be a pipe seperated string i.e. <code>value1|value2|value3</code>
          </p>

          <p>
            <a href="https://documentation.atomic.io/api/action-flow-api">
              Read more about Action Flow input here.
            </a>
          </p>

          <p>
            In test mode the latest configuration will be used (whether it is in
            draft or published) and only test users will be targetted.
          </p>

          <p>
            The maximum file size uploadable is{' '}
            {AF_CSV_MAX_UPLOAD_FILE_SIZE_BYTES / 1024 / 1024}mb.
            <br />A comma (,) must be used as the delimiter between values, and
            files must use utf-8 encoding.
          </p>

          <Button
            appearance="secondary"
            onClick={() => {
              const element = document.createElement('a')
              const now = new Date()

              const sampleCsv = `target.attributes.email,payload.cardOptions.priority,payload.cardOptions.expires,payload.cardOptions.targetStreamIds,payload.metadata.foo,payload.notificationDetail.foo,payload.variables.greeting,flowInvocationId,linked,payload.cardOptions.targetPlatforms
${email},8,${addDays(now, 1).toISOString()},${streamIds.join(
                ARRAY_SEPARATOR
              )},bar,bar,Hello,${v4()},false,Web|iOS|Android
${email},6,${addDays(
                now,
                1
              ).toISOString()},,bar,bar,Hi there,${v4()},true,Web|iOS|Android
${email},8,${addDays(
                now,
                1
              ).toISOString()},,bar,bar,Hi,${v4()},false,Web|iOS|Android`

              const file = new Blob([sampleCsv], {
                type: 'text/csv'
              })
              // eslint-disable-next-line functional/immutable-data
              element.href = URL.createObjectURL(file)
              // eslint-disable-next-line functional/immutable-data
              element.download = `sample_atomic_action_flow_bulk_send.csv`
              document.body.appendChild(element)
              element.click()
            }}
          >
            Download a sample csv file
          </Button>
        </HelpDiv>
      )}
    </>
  )
}
